import { ReactNode, Ref } from 'react';
import styled from 'styled-components';

type Props = {
  title: string;
  description?: ReactNode;
  titleAside?: ReactNode;
  hideTitle?: boolean;
  actions?: ReactNode;
  onBack?: () => void;
  compact?: boolean;
  className?: string;
  elementRef?: Ref<HTMLElement>;
};

export default function AdminPageHeader({ title, description, titleAside, hideTitle = false, actions, onBack, compact = false, className, elementRef }: Props) {
  return <Header ref={elementRef} $compact={compact} className={className} aria-label={hideTitle ? title : undefined}>
    <Heading $compact={compact}>
      {onBack && <BackButton type="button" onClick={onBack} aria-label="이전 화면으로">‹</BackButton>}
      {!hideTitle && <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>}
      {titleAside}
    </Heading>
    {actions && <Actions>{actions}</Actions>}
  </Header>;
}

export const AdminActionButton = styled.button.attrs(props => ({ className: 'admin-header-action', type: props.type ?? 'button' }))<{ $primary?: boolean; $accented?: boolean }>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: var(--control-lg);
  padding: 0 var(--space-4);
  border: 1px solid ${({ $primary, $accented }) => $primary || $accented ? 'var(--color-brand)' : 'var(--admin-theme-control-border, var(--admin-border))'};
  border-radius: var(--radius-md);
  background: ${({ $primary, $accented }) => $primary ? 'var(--gradient-brand)' : $accented ? 'var(--color-brand-subtle)' : 'var(--color-bg-surface)'};
  color: ${({ $primary, $accented }) => $primary ? 'var(--color-text-on-brand)' : $accented ? 'var(--color-text-link)' : 'var(--color-text-primary)'};
  font-family: var(--font-family-base), sans-serif; font-weight: var(--font-weight-semibold);
  font-size: var(--text-body);
  line-height: 1.3;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled), &:active:not(:disabled) {
    border-color: ${({ $primary }) => $primary ? 'var(--admin-accent-deep)' : 'var(--admin-theme-focus, var(--admin-accent))'};
    background: ${({ $primary, $accented }) => $primary ? 'var(--gradient-brand-hover)' : $accented ? 'var(--color-brand-subtle)' : 'var(--color-bg-subtle)'};
  }
  &:focus-visible { outline: 2px solid var(--admin-theme-focus, var(--admin-accent)); outline-offset: 2px; }
  &:disabled { border-color: var(--admin-theme-control-border, var(--admin-border)); background: var(--admin-theme-raised, var(--admin-canvas)); color: var(--admin-muted); cursor: not-allowed; }
`;

const Header = styled.header<{ $compact: boolean }>`
  position: sticky;
  top: 0;
  z-index: 10;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ $compact }) => $compact ? '8px' : '20px'};
  min-height: ${({ $compact }) => $compact ? '56px' : '72px'};
  padding: ${({ $compact }) => $compact ? '12px 16px' : '0 0 20px'};
  margin-bottom: ${({ $compact }) => $compact ? '0' : '24px'};
  border-bottom: 1px solid var(--admin-border);
  background: var(--admin-canvas);

  /* The admin scroll container has 30px top padding. Cover that gap while sticky. */
  &::before {
    content: '';
    position: absolute;
    top: -30px;
    left: -24px;
    right: -24px;
    height: 30px;
    background: var(--admin-canvas);
    pointer-events: none;
  }

  @media (max-width: 640px) {
    flex-wrap: wrap;
    min-height: 0;
  }
`;

const Heading = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  && h1 {
    margin: 0;
    color: var(--admin-ink);
    font-family: var(--font-family-base), sans-serif; font-weight: var(--font-weight-bold);
    font-size: var(--text-h2);
    font-weight: var(--font-weight-semibold);
    line-height: 1.35;
    letter-spacing: var(--tracking-heading);
  }
  && p { margin: 6px 0 0; color: var(--admin-muted); font-size: var(--text-body); line-height: 1.5; }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: var(--space-2);
  min-height: var(--control-lg);
  @media (max-width: 640px) { justify-content: flex-start; width: 100%; }
`;

const BackButton = styled(AdminActionButton)`
  flex: 0 0 var(--control-lg);
  width: var(--control-lg);
  padding: 0;
  font-size: var(--text-display);
  line-height: 1;
`;
